import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../contexts/PermissionContext';
import { PermissionLevel } from '../types/permissions';
import { PermissionDenied } from './PermissionDenied';

interface PermissionRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredPermission?: string;
  requiredLevel?: PermissionLevel;
  fallbackPath?: string;
  showFallbackPage?: boolean;
  showNotFoundPage?: boolean;
}

/**
 * 权限路由保护组件
 * 用于保护路由，只有具备相应权限的用户才能访问
 */
export const PermissionRoute: React.FC<PermissionRouteProps> = ({
  children,
  requiredRoles = [],
  requiredPermission,
  requiredLevel,
  fallbackPath = '/dashboard',
  showFallbackPage = true,
  showNotFoundPage = false,
}) => {
  const { 
    isAdmin, 
    isHQStaff, 
    isWarehouseSupervisor, 
    isWarehouseStaff, 
    isMainAccount, 
    isSubAccount,
    hasPermission,
    loading 
  } = usePermissions();
  const location = useLocation();
  
  // 如果无权限，关闭因路径变更而被 TabsManager 误创建的标签页
  useEffect(() => {
    if (!loading) {
      const canAccess = ((): boolean => {
        const roleChecks = {
          admin: isAdmin(),
          hq_staff: isHQStaff(),
          warehouse_supervisor: isWarehouseSupervisor(),
          warehouse_staff: isWarehouseStaff(),
          main_account: isMainAccount(),
          sub_account: isSubAccount(),
        };
        const roleOk = requiredRoles.length === 0 || requiredRoles.some(r => roleChecks[r as keyof typeof roleChecks]);
        const permOk = requiredPermission ? hasPermission(requiredPermission, requiredLevel) : true;
        return roleOk && permOk;
      })();

      if (!canAccess) {
        try {
          // 标记一次性拦截，供 TabsManager 在本次路径变化中跳过创建标签
          sessionStorage.setItem('deny_once_path', location.pathname);
          // 通过路径关闭当前被误创建的标签页
          // @ts-ignore
          window?.tabsManager?.closeTabByPath?.(location.pathname);
        } catch {}
      }
    }
  }, [loading, requiredRoles, requiredPermission, requiredLevel, isAdmin, isHQStaff, isWarehouseSupervisor, isWarehouseStaff, isMainAccount, isSubAccount, hasPermission, location.pathname]);

  // 如果权限系统还在加载，显示加载状态
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">权限验证中...</div>
      </div>
    );
  }

  // 检查角色权限
  const checkRolePermission = (): boolean => {
    if (requiredRoles.length === 0) return true;

    const roleChecks = {
      admin: isAdmin(),
      hq_staff: isHQStaff(),
      warehouse_supervisor: isWarehouseSupervisor(),
      warehouse_staff: isWarehouseStaff(),
      main_account: isMainAccount(),
      sub_account: isSubAccount(),
    };

    return requiredRoles.some(role => roleChecks[role as keyof typeof roleChecks]);
  };

  // 检查功能权限
  const checkFunctionPermission = (): boolean => {
    if (!requiredPermission) return true;
    return hasPermission(requiredPermission, requiredLevel);
  };

  // 综合权限检查
  const hasAccess = checkRolePermission() && checkFunctionPermission();

  // 如果没有权限
  if (!hasAccess) {
    // 可选：显示404样式页面，用户手动关闭
    if (showNotFoundPage) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-4xl font-bold mb-3">404</div>
            <div className="text-lg mb-2">无权限访问该页面</div>
            <div className="text-sm text-gray-500">请手动关闭本标签页</div>
          </div>
        </div>
      );
    }

    // 如果设置了显示fallback页面，显示权限拒绝页面
    if (showFallbackPage) {
      return (
        <PermissionDenied
          currentPath={location.pathname}
          requiredRoles={requiredRoles}
          requiredPermission={requiredPermission}
          fallbackPath={fallbackPath}
        />
      );
    }

    // 否则重定向到指定路径
    return <Navigate to={fallbackPath} replace />;
  }

  // 有权限，渲染子组件
  return <>{children}</>;
};

// 权限重定向组件
interface PermissionRedirectProps {
  to: string;
  requiredPermission?: string;
  requiredLevel?: PermissionLevel;
  requiredRoles?: string[];
  fallbackPath?: string;
}

export const PermissionRedirect: React.FC<PermissionRedirectProps> = ({
  to,
  requiredPermission,
  requiredLevel,
  requiredRoles,
  fallbackPath,
}) => {
  const { 
    hasPermission, 
    loading, 
    isAdmin, 
    isHQStaff, 
    isWarehouseSupervisor, 
    isWarehouseStaff, 
    isMainAccount, 
    isSubAccount 
  } = usePermissions();

  if (loading) {
    return <div>加载中...</div>;
  }

  // 检查角色权限
  const checkRoles = (): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    const roleChecks = {
      admin: isAdmin(),
      hq_staff: isHQStaff(),
      warehouse_supervisor: isWarehouseSupervisor(),
      warehouse_staff: isWarehouseStaff(),
      main_account: isMainAccount(),
      sub_account: isSubAccount(),
    };

    return requiredRoles.some(role => roleChecks[role as keyof typeof roleChecks]);
  };

  // 检查权限
  const hasRequiredPermission = requiredPermission 
    ? hasPermission(requiredPermission, requiredLevel)
    : true;

  const hasRequiredRole = checkRoles();

  // 决定重定向路径
  const redirectPath = (hasRequiredPermission && hasRequiredRole) ? to : (fallbackPath || '/');

  return <Navigate to={redirectPath} replace />;
};

// 权限链接组件
interface PermissionLinkProps {
  to: string;
  children: React.ReactNode;
  requiredPermission?: string;
  requiredLevel?: PermissionLevel;
  requiredRoles?: string[];
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

export const PermissionLink: React.FC<PermissionLinkProps> = ({
  to,
  children,
  requiredPermission,
  requiredLevel,
  requiredRoles,
  className,
  style,
  onClick,
}) => {
  const { 
    hasPermission, 
    loading, 
    isAdmin, 
    isHQStaff, 
    isWarehouseSupervisor, 
    isWarehouseStaff, 
    isMainAccount, 
    isSubAccount 
  } = usePermissions();

  if (loading) {
    return <span className={className} style={style}>加载中...</span>;
  }

  // 检查角色权限
  const checkRoles = (): boolean => {
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    const roleChecks = {
      admin: isAdmin(),
      hq_staff: isHQStaff(),
      warehouse_supervisor: isWarehouseSupervisor(),
      warehouse_staff: isWarehouseStaff(),
      main_account: isMainAccount(),
      sub_account: isSubAccount(),
    };

    return requiredRoles.some(role => roleChecks[role as keyof typeof roleChecks]);
  };

  // 检查权限
  const hasRequiredPermission = requiredPermission 
    ? hasPermission(requiredPermission, requiredLevel)
    : true;

  const hasRequiredRole = checkRoles();

  // 如果没有权限，禁用链接
  if (!hasRequiredPermission || !hasRequiredRole) {
    return (
      <span 
        className={`${className || ''} disabled-link`} 
        style={{ 
          ...style, 
          color: '#ccc', 
          cursor: 'not-allowed',
          textDecoration: 'none'
        }}
        title="您没有访问此功能的权限"
      >
        {children}
      </span>
    );
  }

  // 有权限，正常链接
  return (
    <a 
      href={to}
      className={className}
      style={style}
      onClick={onClick}
    >
      {children}
    </a>
  );
};

export default PermissionRoute;
